// Component: BacktestAnalyticsPanel.jsx
// Purpose: Analytics panel — performance metrics, equity curve, commission breakdown, skipped trades
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import BacktestMetrics from './BacktestMetrics';
import BacktestEquityCurve from './BacktestEquityCurve';

const asNum = (v, fallback = 0) => { const n = Number(v); return Number.isFinite(n) ? n : fallback; };

// --> Commission & F&O summary tile
const SummaryTile = ({ label, value, sub, colorClass = 'text-fg-1' }) => (
    <div className="bg-bg-3 border border-[var(--divider)] rounded-xl p-3 shadow-sm">
        <p className="text-[9px] font-bold text-fg-3 uppercase tracking-widest mb-1">{label}</p>
        <p className={`text-base font-black font-mono ${colorClass}`}>{value}</p>
        {sub && <p className="text-[9px] text-fg-3 mt-0.5">{sub}</p>}
    </div>
);

const BacktestAnalyticsPanel = ({ metrics, chartData, buyHoldData, initialCapital, strategyId = null, compact = 'auto' }) => {
    const containerRef = useRef(null);
    const [autoCompact, setAutoCompact] = useState(false);

    const isCompact = useMemo(() => {
        if (compact === true) return true;
        if (compact === false) return false;
        return autoCompact;
    }, [compact, autoCompact]);

    useEffect(() => {
        if (compact !== 'auto') return undefined;
        if (typeof ResizeObserver === 'undefined' || !containerRef.current) return undefined;

        const target = containerRef.current;
        const updateCompactState = (width) => {
            setAutoCompact(width < 980);
        };

        updateCompactState(target.clientWidth || 0);

        const resizeObserver = new ResizeObserver((entries) => {
            const entry = entries[0];
            if (!entry) return;
            updateCompactState(entry.contentRect.width);
        });
        resizeObserver.observe(target);

        return () => {
            resizeObserver.disconnect();
        };
    }, [compact]);

    if (!metrics && (!chartData || chartData.length === 0)) {
        return (
            <div ref={containerRef} className="h-full flex items-center justify-center text-sm font-medium text-fg-3">
                No analytics data available yet.
            </div>
        );
    }

    // --> Extract commission / F&O aggregates from metrics (added by backtest_runner)
    const totalCommission   = asNum(metrics?.total_commission ?? metrics?.totalCommission);
    const totalBrokerage    = asNum(metrics?.total_brokerage ?? metrics?.totalBrokerage);
    //--> R2-F2: API field only; null (older stored results) reads 'Unresolved', never a derived or zero value.
    const statutoryRaw      = metrics?.total_statutory_charges ?? metrics?.totalStatutoryCharges;
    const totalStatutory    = statutoryRaw === null || statutoryRaw === undefined ? null : asNum(statutoryRaw);
    const totalLots         = asNum(metrics?.total_lots_traded ?? metrics?.totalLotsTraded);
    const skippedCount      = asNum(metrics?.skipped_trades_count ?? metrics?.skippedTradesCount);
    const hasCommissionData = metrics && (totalCommission > 0 || totalLots > 0 || skippedCount > 0);

    return (
        <div ref={containerRef} className="w-full h-full flex flex-col gap-4 p-3 md:p-4 bg-bg-2">
            {metrics && <BacktestMetrics metrics={metrics} />}

            {/* --> Commission & F&O breakdown summary — fluid collapse/expand */}
            <AnimatePresence initial={false}>
                {hasCommissionData && (
                    <motion.div
                        key="commission-summary"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
                        className="overflow-hidden"
                    >
                        <div className="border border-[var(--divider)] rounded-2xl p-4 bg-bg-2 shadow-sm">
                            <h3 className="ds-eyebrow text-fg-2 mb-3">Commission &amp; F&amp;O Summary</h3>
                            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
                                <SummaryTile
                                    label="Total Commission"
                                    value={`₹${totalCommission.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`}
                                    colorClass="text-[var(--warning)]"
                                />
                                <SummaryTile
                                    label="Brokerage"
                                    value={`₹${totalBrokerage.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`}
                                    sub="Broker fees"
                                    colorClass="text-fg-1"
                                />
                                <SummaryTile
                                    label="Statutory Charges"
                                    value={totalStatutory === null ? 'Unresolved' : `₹${totalStatutory.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`}
                                    sub="STT + SEBI + GST etc."
                                    colorClass="text-[var(--warning)]"
                                />
                                <SummaryTile
                                    label="Total Lots Traded"
                                    value={totalLots.toLocaleString('en-IN')}
                                    sub="Sum across all trades"
                                    colorClass="text-[var(--node-comparator)]"
                                />
                                <SummaryTile
                                    label="Skipped Trades"
                                    value={skippedCount}
                                    sub={skippedCount > 0 ? 'Insufficient margin' : 'None skipped'}
                                    colorClass={skippedCount > 0 ? 'text-[var(--color-loss)]' : 'text-[var(--color-profit)]'}
                                />
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            <div className="border border-[var(--divider)] rounded-2xl p-4 bg-bg-2 shadow-sm">
                <h3 className="ds-eyebrow text-fg-2 mb-4">Equity Curve</h3>
                <BacktestEquityCurve
                    data={chartData}
                    buyHoldData={buyHoldData}
                    initialCapital={initialCapital ?? metrics?.initial_capital}
                    heightClass={isCompact ? 'h-72' : 'h-96'}
                    strategyId={strategyId}
                />
            </div>
        </div>
    );
};

export default BacktestAnalyticsPanel;
